import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import {
  parseErrorResponse,
  type AnyException,
} from "byzantine/src/dbbl/composites/request/request.helpers";
import type { ErrorResponse } from "byzantine/src/dbbl/composites/request/request.types";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import type { DualApprovalRejectApproveDialogType } from "../common/DualApprovalRejectApproveDialog";

type NotificationKeys = {
  approve: string;
  reject: string;
};

type UseDualApprovalActionsParams = {
  selectedApprovals: PaginatedDualApproval[];
  totalSelected: number;
  handleRefresh: () => Promise<API.FetchDualApproval.Response | undefined>;
  notificationKeys: NotificationKeys;
  onSelectionReset: (failedIds: string[]) => void;
  onDeselectItem: (id: string) => void;
};

type UseDualApprovalActionsReturn = {
  isActionLoading: boolean;
  toggledDialogName: DualApprovalRejectApproveDialogType["type"];
  toggleDialog: (name: DualApprovalRejectApproveDialogType["type"]) => void;
  handleAction: (action: "approve" | "reject", note?: string) => Promise<void>;
  handleDrawerAction: (params: {
    action: "approve" | "reject";
    note?: string;
    activeDualApproval: PaginatedDualApproval | null;
    items: PaginatedDualApproval[];
    selectedIndex: number;
    getPathWithDrawer: (id: string) => string;
    navigate: (path: string, options?: { replace?: boolean }) => void;
    handleCloseDrawer: () => void;
  }) => Promise<void>;
};

const useDualApprovalActions = ({
  selectedApprovals,
  totalSelected,
  handleRefresh,
  notificationKeys,
  onSelectionReset,
  onDeselectItem,
}: UseDualApprovalActionsParams): UseDualApprovalActionsReturn => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { send: approveDualApproval } =
    entities.dualApprovals.useApproveDualApproval();
  const { send: rejectDualApproval } =
    entities.dualApprovals.useRejectDualApproval();

  const [isActionLoading, setIsActionLoading] = useState(false);
  const [toggledDialogName, setToggledDialogName] =
    useState<DualApprovalRejectApproveDialogType["type"]>("");

  const toggleDialog = (
    dialogName: DualApprovalRejectApproveDialogType["type"]
  ) => {
    setToggledDialogName(dialogName);
  };

  const notifyActionResult = ({
    action,
    successCount,
    hasFailure,
    errorMessage,
  }: {
    action: "approve" | "reject";
    successCount: number;
    hasFailure: boolean;
    errorMessage?: string;
  }) => {
    if (successCount > 0) {
      const actionKey =
        action === "approve"
          ? notificationKeys.approve
          : notificationKeys.reject;

      sendNotification({
        type: "success",
        text: l10n.getString(actionKey, { numSelected: successCount }),
      });
    }

    if (hasFailure) {
      sendNotification({
        type: "negative",
        text: errorMessage || l10n.getString("error-generic"),
      });
    }
  };

  const refreshApprovals = async () => {
    try {
      return await handleRefresh();
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("error-generic"),
      });
      return undefined;
    }
  };

  const handleAction = async (action: "approve" | "reject", note?: string) => {
    if (!totalSelected || isActionLoading) {
      return;
    }

    setIsActionLoading(true);

    const requests = selectedApprovals.map(({ id }) =>
      action === "approve"
        ? approveDualApproval(id)
        : rejectDualApproval(id, note)
    );

    const results = await Promise.allSettled(requests);
    const failedResults = results.filter(
      (r): r is PromiseRejectedResult => r.status === "rejected"
    );
    const failedIds = selectedApprovals
      .filter((_, index) => results[index].status === "rejected")
      .map(({ id }) => id);
    const successCount = results.length - failedIds.length;

    let errorMessage: string | undefined;
    if (failedResults.length > 0) {
      try {
        const parsed = await parseErrorResponse(failedResults[0].reason);
        errorMessage = parsed.errors[0]?.description;
      } catch {
        // fall back to generic error
      }
    }

    notifyActionResult({
      action,
      successCount,
      hasFailure: failedIds.length > 0,
      errorMessage,
    });

    onSelectionReset(failedIds);

    try {
      await refreshApprovals();
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDrawerAction = async ({
    action,
    note,
    activeDualApproval,
    items,
    selectedIndex,
    getPathWithDrawer,
    navigate,
    handleCloseDrawer,
  }: {
    action: "approve" | "reject";
    note?: string;
    activeDualApproval: PaginatedDualApproval | null;
    items: PaginatedDualApproval[];
    selectedIndex: number;
    getPathWithDrawer: (id: string) => string;
    navigate: (path: string, options?: { replace?: boolean }) => void;
    handleCloseDrawer: () => void;
  }) => {
    if (!activeDualApproval || isActionLoading) {
      return;
    }

    setIsActionLoading(true);

    let actionSucceeded = false;

    try {
      if (action === "approve") {
        await approveDualApproval(activeDualApproval.id);
      } else {
        await rejectDualApproval(activeDualApproval.id, note);
      }

      actionSucceeded = true;

      notifyActionResult({
        action,
        successCount: 1,
        hasFailure: false,
      });

      onDeselectItem(activeDualApproval.id);

      const nextDualApprovalId =
        selectedIndex > -1
          ? items[selectedIndex + 1]?.id ?? items[selectedIndex - 1]?.id
          : undefined;

      if (nextDualApprovalId) {
        navigate(getPathWithDrawer(nextDualApprovalId), {
          replace: true,
        });
      }
    } catch (err) {
      let errorMessage: string | undefined;
      try {
        const parsed = await parseErrorResponse(
          err as ErrorResponse<AnyException>
        );
        errorMessage = parsed.errors[0]?.description;
      } catch {
        // fall back to generic error
      }

      notifyActionResult({
        action,
        successCount: 0,
        hasFailure: true,
        errorMessage,
      });
    } finally {
      if (actionSucceeded) {
        const response = await refreshApprovals();

        let nextDualApprovalId =
          selectedIndex > -1
            ? items[selectedIndex + 1]?.id ?? items[selectedIndex - 1]?.id
            : undefined;

        if (!nextDualApprovalId) {
          nextDualApprovalId = response?.approval_requests?.[0]?.uuid;
        }

        if (nextDualApprovalId) {
          navigate(getPathWithDrawer(nextDualApprovalId), {
            replace: true,
          });
        } else {
          handleCloseDrawer();
        }
      }

      setIsActionLoading(false);
    }
  };

  return {
    isActionLoading,
    toggledDialogName,
    toggleDialog,
    handleAction,
    handleDrawerAction,
  };
};

export default useDualApprovalActions;
